import React from "react";
import { Capacitor } from "@capacitor/core";
import { fromDateOnly } from "@/lib/dateUtils";
import { SUBSCRIPTION_CHECKOUT_URL } from "@/config/subscription";
import { getStoreManageUrl, STORE_MANAGE_LABEL } from "@/lib/storeSubscriptionLinks";
import { useSubscriptionProvider } from "@/hooks/useSubscriptionProvider";
import { openExternalUrl } from "@/utils/linkify";
import { hebrewDaysPhrase } from "@/lib/hebrewPlural";
import { useNavigate } from "react-router-dom";
import { useIapEnabled } from "@/hooks/useIapEnabled";
import { IN_APP_PAYWALL_PATH } from "@/lib/inAppSubscribe";

interface GracePeriodBannerProps {
  graceEndDate: string;
}

export function GracePeriodBanner({ graceEndDate }: GracePeriodBannerProps) {
  const isNative = Capacitor.isNativePlatform();
  const storeManageUrl = getStoreManageUrl(useSubscriptionProvider());
  const { enabled: iapEnabled } = useIapEnabled();
  const navigate = useNavigate();

  // graceEndDate הוא YYYY-MM-DD מהדאטאבייס. חייבים לפרש כחצות מקומי
  // (לא UTC) כדי שמספר הימים שנותרו לא יזוז ביום אחורה אצל משתמשים בישראל.
  const endDate = fromDateOnly(graceEndDate);
  const daysLeft = endDate
    ? Math.max(0, Math.ceil((endDate.getTime() - Date.now()) / 86400000))
    : 0;

  // CTA policy:
  //   web                          — unchanged, the existing checkout.
  //   native + store-billed        — the store's own subscription screen. 3.1.1
  //                                  permits it, and it is the only place an
  //                                  Apple/Google-billed member can actually act.
  //   native + web-billed/unknown  — the in-app paywall (a store purchase) when
  //                                  store purchases are live; otherwise no CTA,
  //                                  since every link we own leads to our own
  //                                  checkout, the anti-steering violation itself.
  const cta = !isNative
    ? { label: "הצטרפות למנוי", href: SUBSCRIPTION_CHECKOUT_URL, internal: false }
    : storeManageUrl
      ? { label: STORE_MANAGE_LABEL, href: storeManageUrl, internal: false }
      : iapEnabled
        ? { label: "הצטרפות למנוי", href: IN_APP_PAYWALL_PATH, internal: true }
        : null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mx-4 mb-4">
      <p className="text-sm font-medium text-amber-800 text-start">
        {daysLeft > 0
          ? `${daysLeft === 1 ? 'נשאר' : 'נשארו'} לך ${hebrewDaysPhrase(daysLeft)} להצטרף למנוי ולהמשיך ליהנות מכל התכנים`
          : 'זה היום האחרון! הצטרפו למנוי כדי להמשיך'}
      </p>
      {cta && (
        <button
          type="button"
          // openExternalUrl (Capacitor Browser.open on native) — a bare
          // window.open to a cross-origin host is silently swallowed in the
          // WebView, so the store page would never actually open.
          onClick={() => (cta.internal ? navigate(cta.href) : void openExternalUrl(cta.href))}
          className="mt-2 text-sm font-semibold text-amber-900 underline"
        >
          {cta.label}
        </button>
      )}
    </div>
  );
}
